import {useCallback, useEffect, useRef, useState, type RefObject} from 'react'

export interface TurnstileHook {
    token: string | null
    containerRef: RefObject<HTMLDivElement | null>
    reset: () => void
}

const DISABLED_TOKEN = 'disabled'
const POLL_INTERVAL_MS = 100

/**
 * Renders a Cloudflare Turnstile widget into `containerRef` and exposes its
 * token. When `window.turnstileEnabled` is false (captcha off in this
 * environment), returns a fixed truthy token immediately so callers never
 * need to special-case the disabled state — `token != null` is always the
 * correct "may proceed" check.
 *
 * Turnstile tokens are single-use and short-lived — call `reset()` after any
 * failed submit attempt so the widget re-arms before the next one.
 */
export function useTurnstile(): TurnstileHook {
    const [token, setToken] = useState<string | null>(window.turnstileEnabled ? null : DISABLED_TOKEN)
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | undefined>(undefined)

    useEffect(() => {
        if (!window.turnstileEnabled || containerRef.current == null) {
            return
        }

        let cancelled = false
        let intervalId: ReturnType<typeof setInterval> | undefined

        const render = (): void => {
            if (cancelled || containerRef.current == null || window.turnstile == null) {
                return
            }

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: window.turnstileSiteKey,
                appearance: 'interaction-only',
                size: 'flexible',
                callback: (value) => {
                    setToken(value)
                },
                'error-callback': () => {
                    setToken(null)
                },
                'expired-callback': () => {
                    setToken(null)
                },
            })
        }

        if (window.turnstile != null) {
            render()
        } else {
            intervalId = setInterval(() => {
                if (window.turnstile != null) {
                    clearInterval(intervalId)
                    render()
                }
            }, POLL_INTERVAL_MS)
        }

        return () => {
            cancelled = true
            clearInterval(intervalId)

            if (widgetIdRef.current != null) {
                window.turnstile?.remove(widgetIdRef.current)
            }
        }
    }, [])

    const reset = useCallback((): void => {
        if (!window.turnstileEnabled) {
            return
        }

        setToken(null)

        if (widgetIdRef.current != null) {
            window.turnstile?.reset(widgetIdRef.current)
        }
    }, [])

    return {token, containerRef, reset}
}
